(function () {
  'use strict';

  /**
   * Wait for a DOM element to appear using MutationObserver.
   * Falls back gracefully if the element is already present.
   *
   * @param {string} selector - CSS selector to watch for
   * @param {number} [timeout=5000] - Max ms to wait before giving up
   * @returns {Promise<Element>} - Resolves with the first matching element
   */
  function waitForElement(selector, timeout = 5000) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(selector);
      if (existing) return resolve(existing);

      const observer = new MutationObserver(() => {
        const el = document.querySelector(selector);
        if (el) {
          observer.disconnect();
          resolve(el);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      setTimeout(() => {
        observer.disconnect();
        reject(new Error(`[A11y] Timed out waiting for: "${selector}"`));
      }, timeout);
    });
  }

  function waitForElementInShadow(hostSelector, innerSelector, timeout = 5000) {
    return new Promise((resolve, reject) => {
        // First get the shadow host — it's in the regular DOM
        const host = document.querySelector(hostSelector);
        if (!host) return reject(new Error(`[A11y] Shadow host not found: "${hostSelector}"`));

        const root = host.shadowRoot;
        if (!root) return reject(new Error(`[A11y] No shadowRoot on: "${hostSelector}"`));

        // Check if it's already there
        const existing = root.querySelector(innerSelector);
        if (existing) return resolve(existing);

        // Observe the shadow root directly
        const observer = new MutationObserver(() => {
        const el = root.querySelector(innerSelector);
        if (el) {
            observer.disconnect();
            resolve(el);
        }
        });

        observer.observe(root, { childList: true, subtree: true });

        setTimeout(() => {
        observer.disconnect();
        reject(new Error(`[A11y] Timed out waiting for "${innerSelector}" in shadow root of "${hostSelector}"`));
        }, timeout);
    });
    }

  /**
   * Apply a single accessibility rule to all matching elements.
   *
   * Supported rule properties:
   *   selector    {string}           Required. CSS selector for target elements.
   *   label       {string|Function}  Sets aria-label. Pass a function for dynamic values:
   *                                  (el, index) => `Item ${index + 1}`
   *   labelledby  {string}           Sets aria-labelledby (ID reference).
   *   describedby {string}           Sets aria-describedby (ID reference).
   *   role        {string}           Sets the ARIA role attribute.
   *   name        {string}           Sets the `name` attribute (e.g. on inputs, iframes).
   *   title       {string}           Sets the `title` attribute.
   *   hidden      {boolean}          Sets aria-hidden="true" when true.
   *   expanded    {boolean|string}   Sets aria-expanded.
   *   pressed     {boolean|string}   Sets aria-pressed.
   *   current     {boolean|string}   Sets aria-current.
   *   live        {string}           Sets aria-live ("polite" | "assertive" | "off").
   *   atomic      {boolean}          Sets aria-atomic.
   *   tabindex    {number}           Sets tabindex.
   *   attributes  {Object}           Freeform key/value pairs — sets any attribute.
   *                                  Values can be strings or (el, index) => string functions.
   *   waitFor     {string|boolean}   CSS selector to wait for before applying this rule.
   *                                  Pass `true` to use the rule's own selector as the trigger.
   *   waitTimeout {number}           Override timeout (ms) for this rule's waitFor. Default: 5000.
   *   callback    {Function}         Optional. Called after all elements are processed.
   *                                  Receives the NodeList of matched elements.
   *
   * @param {Object} rule
   */
  async function applyRule(rule) {
    const {
      selector,
      label,
      labelledby,
      describedby,
      role,
      name,
      title,
      hidden,
      expanded,
      pressed,
      current,
      live,
      atomic,
      tabindex,
      attributes = {},
      waitFor,
      waitTimeout = 5000,
      callback,
    } = rule;

    if (!selector) {
      console.warn('[A11y] Rule is missing a selector:', rule);
      return;
    }

    // If waitFor is set, pause until the trigger element is present
    if (waitFor) {
      const waitSelector = waitFor === true ? selector : waitFor;
      try {
        await waitForElement(waitSelector, waitTimeout);
      } catch (err) {
        console.warn(err.message);
        return; // Bail — element never appeared
      }
    }

    const elements = document.querySelectorAll(selector);
    if (!elements.length) {
      console.warn(`[A11y] No elements found for selector: "${selector}"`);
      return;
    }

    elements.forEach((el, index) => {
      // aria-label (supports static string, dynamic function, or async function)
      if (label !== undefined) {
        const resolvedLabel =
          typeof label === 'function' ? label(el, index) : label;
        Promise.resolve(resolvedLabel).then((val) => {
          if (val) el.setAttribute('aria-label', val);
        });
      }

      if (labelledby !== undefined) el.setAttribute('aria-labelledby', labelledby);
      if (describedby !== undefined) el.setAttribute('aria-describedby', describedby);
      if (role !== undefined) el.setAttribute('role', role);
      if (name !== undefined) el.setAttribute('name', name);
      if (title !== undefined) el.setAttribute('title', title);

      if (hidden !== undefined) el.setAttribute('aria-hidden', String(hidden));
      if (expanded !== undefined) el.setAttribute('aria-expanded', String(expanded));
      if (pressed !== undefined) el.setAttribute('aria-pressed', String(pressed));
      if (current !== undefined) el.setAttribute('aria-current', String(current));
      if (live !== undefined) el.setAttribute('aria-live', live);
      if (atomic !== undefined) el.setAttribute('aria-atomic', String(atomic));
      if (tabindex !== undefined) el.setAttribute('tabindex', String(tabindex));

      // Freeform attributes — supports static values or (el, index) => value functions
      Object.entries(attributes).forEach(([attr, val]) => {
        el.setAttribute(attr, typeof val === 'function' ? val(el, index) : val);
      });
    });

    if (typeof callback === 'function') callback(elements);
  }

  /**
   * Main initializer. Call this with your config object.
   *
   * @param {Object} config
   * @param {Object[]} config.rules       - Array of rule objects (see applyRule above)
   * @param {boolean}  [config.debug]     - Log applied rules to console
   */
  function init({ rules = [], debug = false } = {}) {
    if (!rules.length) {
      console.warn('[A11y] No rules provided.');
      return;
    }

    function run() {
      rules.forEach((rule) => {
        if (debug) console.log('[A11y] Applying rule:', rule.selector);
        applyRule(rule).catch((err) => console.error('[A11y] Rule error:', err));
      });
    }

    // Defer execution — runs after the browser has painted
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      // Already loaded — defer to next event loop tick to avoid blocking render
      requestIdleCallback ? requestIdleCallback(run) : setTimeout(run, 0);
    }
  }

  // Expose globally
  window.AccessibilityEnhancer = { init, waitForElement, applyRule };
})();


// ---------------------------------------------------------------------------
// Example configuration — customize or move to a separate file/section
// ---------------------------------------------------------------------------

AccessibilityEnhancer.init({
  debug: true, // Set true to log rule processing in the console
  rules: [
    {
      // Product form — wait for it to be injected (e.g. via section rendering API)
      selector: '.acsb-trigger.acsb-widget',
      label: 'Accessibility Button',
      waitFor: '.acsb-trigger.acsb-widget', // Wait for the form wrapper first
      waitTimeout: 8000,
    },{
      // Product form — wait for it to be injected (e.g. via section rendering API)
      selector: '.ot-sdk-show-settings',
      label: 'Manage Settings',
      waitFor: '.ot-sdk-show-settings', // Wait for the form wrapper first
      waitTimeout: 8000,
    },
    {
    selector: '[data-acsb="skipLinks"]',
    waitFor: true,
    callback: async (elements) => {
        const host = elements[0];
        const root = host.shadowRoot;
        if (!root) return;

        const skipLink = root.querySelector('.skip-link');
        if (!skipLink) return;

        skipLink.setAttribute('aria-label', skipLink.innerText.trim());
    },
    },
    // {
    //   selector: '.skip-link',
    //   label: "SKIP LINK",
    // //   label: (el) => el.innerText.trim(),
    //   waitFor: '.skip-link', // Wait for the form wrapper first
    //   waitTimeout: 8000,
    // },
  ]
})

// AccessibilityEnhancer.init({
//   debug: false, // Set true to log rule processing in the console

//   rules: [

//     // --- Static elements present on page load ---

//     {
//       // Give the main site logo a descriptive label
//       selector: '.header__logo a',
//       label: 'Go to homepage',
//       role: 'link',
//     },

//     {
//       // Label the cart icon button
//       selector: '[data-cart-toggle], .cart-icon-bubble',
//       label: 'Open cart',
//       attributes: { 'aria-haspopup': 'dialog' },
//     },

//     {
//       // Label nav toggle for mobile
//       selector: '.header__icon--menu',
//       label: 'Open navigation menu',
//       attributes: { 'aria-haspopup': 'true', 'aria-controls': 'main-nav' },
//     },

//     {
//       // Hide decorative SVG icons from screen readers
//       selector: '.icon, svg[aria-hidden!="false"]',
//       hidden: true,
//     },

//     {
//       // Each product card image gets a dynamic label based on alt text or position
//       selector: '.card__media img',
//       label: (el, index) => el.getAttribute('alt') || `Product image ${index + 1}`,
//     },

//     // --- Elements that may render dynamically (apps, sections, AJAX) ---

//     {
//       // Product form — wait for it to be injected (e.g. via section rendering API)
//       selector: '.product-form__submit',
//       label: 'Add to cart',
//       waitFor: '.product-form', // Wait for the form wrapper first
//       waitTimeout: 8000,
//     },

//     {
//       // Quantity input — common in dynamic product sections
//       selector: '.quantity__input',
//       label: 'Product quantity',
//       waitFor: true, // Use the selector itself as the trigger
//     },

//     {
//       // Slideshow pagination dots — wait for the slider to init
//       selector: '.slideshow__control',
//       label: (el, index) => `Go to slide ${index + 1}`,
//       role: 'tab',
//       waitFor: '.slideshow',
//     },

//     {
//       // Label iframes (e.g. embedded video players)
//       selector: '.media iframe',
//       title: 'Product video',
//       name: 'product-video',
//     },

//     {
//       // Announcement bar — mark as a live region so updates are announced
//       selector: '.announcement-bar',
//       live: 'polite',
//       atomic: true,
//     },

//   ],
// });